import React, { useState, useEffect } from 'react';
import {
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@material-ui/core';
import {
  KeyboardTimePicker,
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from '@material-ui/pickers';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import moment from 'moment';
import { RemoveCircle } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import CustomSelect from '../../../../shared/form-controls/custom-select/Custom-Select';
import CustomTextField from '../../../../shared/form-controls/custom-text-field/Custom-Text-Field';
import { DAYS } from '../../../../constants/OfferConst';
import { MAINTENANCE_RECURS_OPTIONS } from '../../../../constants/ActivityCalendarConst';
import { getDaysOfWeekFromRange } from '../../../../utils/formUtils';
import MomentUtils from '@date-io/moment';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';

const ActivityMaintenance = ({
  maintenanceFormData,
  setMaintenanceFormData,
  maintenanceSlots,
  setMaintenanceSlots,
  scheduleFormData,
}) => {
  const [isEditing, setIsEditing] = useState({ isEdit: false, index: -1 });
  const [newTimeSlot, setNewTimeSlot] = useState({
    startTime: moment().format('HH:mm'),
    endTime: moment().add(1, 'hour').format('HH:mm'),
  });
  const [duplicateError, setDuplicateError] = useState(false);
  const [overlapDialog, setOverlapDialog] = useState({ open: false, slot: null });
  const [days, setDays] = useState(
    DAYS.map((day) => ({ ...day, ischecked: false, isDisable: false }))
  );

  const {
    maintenanceStatus,
    maintenanceType,
    startDate,
    endDate,
    startTime,
    endTime,
    recurs,
    recursWeek,
    recursMonth,
  } = maintenanceFormData;

  const getActivityDateRange = () => {
    const availableOn = scheduleFormData?.availableOn?.value;
    if (availableOn === 'alwaysActive') return { start: null, end: null };
    const scheduleStartDate = scheduleFormData?.startDate?.value;
    // One-time activities have a single date (endDate holds a stale default there)
    const scheduleEndDate =
      availableOn === 'recurring'
        ? scheduleFormData?.endDate?.value || scheduleStartDate
        : scheduleStartDate;
    return {
      start: scheduleStartDate ? moment(scheduleStartDate, 'DD-MM-YYYY') : null,
      end: scheduleEndDate ? moment(scheduleEndDate, 'DD-MM-YYYY') : null,
    };
  };

  const setTimeFormat = (time) => {
    if (time === 'all day' || time === '24*7') return time;
    return moment(time, 'HH:mm').format('hh:mm A');
  };

  const getOrdinalSuffix = (num) => {
    const j = num % 10,
      k = num % 100;
    if (k >= 11 && k <= 13) return num + 'th';
    switch (j) {
      case 1: return num + 'st';
      case 2: return num + 'nd';
      case 3: return num + 'rd';
      default: return num + 'th';
    }
  };

  const formatSlotDisplay = (slot) => {
    const timeRange = `${setTimeFormat(slot.startTime)} - ${setTimeFormat(slot.endTime)}`;
    const formattedEndDate = endDate.value ? moment(endDate.value, 'DD-MM-YYYY').format('DD/MM/YYYY') : '';

    if (recurs.value === 'DAY') {
      return `Everyday until ${formattedEndDate} | ${timeRange}`;
    } else if (recurs.value === 'WEEK') {
      const selectedDayNames = days
        .filter((d) => d.ischecked)
        .map((d) => d.name.substring(0, 3))
        .join(', ');
      return `${selectedDayNames || 'No days selected'} | ${timeRange} until ${formattedEndDate}`;
    } else if (recurs.value === 'MONTH') {
      const monthDates = recursMonth.value
        ? recursMonth.value
            .split(',')
            .map((d) => getOrdinalSuffix(parseInt(d.trim())))
            .join(', ')
        : '';
      return `${monthDates || 'No dates selected'} | ${timeRange} until ${formattedEndDate}`;
    }
    return timeRange;
  };

  const checkDuplicateSlot = (startTime, endTime, excludeIndex = -1) => {
    return maintenanceSlots.some((slot, index) => {
      if (excludeIndex !== -1 && index === excludeIndex) return false;
      return slot.startTime === startTime && slot.endTime === endTime;
    });
  };

  const timeRangesOverlap = (aStart, aEnd, bStart, bEnd) => {
    const toMinutes = (t) => {
      const m = moment(t, 'HH:mm', true);
      return m.isValid() ? m.hours() * 60 + m.minutes() : null;
    };
    const as = toMinutes(aStart);
    let ae = toMinutes(aEnd);
    const bs = toMinutes(bStart);
    let be = toMinutes(bEnd);
    if ([as, ae, bs, be].some((v) => v === null)) return false;
    if (ae <= as) ae += 1440;
    if (be <= bs) be += 1440;
    return [-1440, 0, 1440].some((shift) => as < be + shift && ae > bs + shift);
  };

  const checkOverlapWithActivitySchedule = (maintStartTime, maintEndTime) => {
    const activityStartTime = scheduleFormData?.startTime?.value;
    const activityEndTime = scheduleFormData?.endTime?.value;

    if (!activityStartTime || !activityEndTime) return false;

    return timeRangesOverlap(maintStartTime, maintEndTime, activityStartTime, activityEndTime);
  };

  const addSlotToList = (slot) => {
    if (isEditing.isEdit) {
      const updatedSlots = [...maintenanceSlots];
      updatedSlots[isEditing.index] = slot;
      setMaintenanceSlots(updatedSlots);
      setIsEditing({ isEdit: false, index: -1 });
    } else {
      setMaintenanceSlots([...maintenanceSlots, slot]);
    }

    setNewTimeSlot({
      startTime: moment().format('HH:mm'),
      endTime: moment().add(1, 'hour').format('HH:mm'),
    });
  };

  const handleAddTimeSlot = () => {
    if (!newTimeSlot.startTime || !newTimeSlot.endTime) return;

    const isDuplicate = checkDuplicateSlot(
      newTimeSlot.startTime,
      newTimeSlot.endTime,
      isEditing.isEdit ? isEditing.index : -1
    );

    if (isDuplicate) {
      setDuplicateError(true);
      return;
    }

    setDuplicateError(false);

    const slot = {
      startTime: newTimeSlot.startTime,
      endTime: newTimeSlot.endTime,
    };

    // Check if maintenance time overlaps with activity schedule time
    const hasOverlap = checkOverlapWithActivitySchedule(slot.startTime, slot.endTime);
    if (hasOverlap) {
      setOverlapDialog({ open: true, slot });
      return;
    }

    addSlotToList(slot);
  };

  const handleOverlapDialogClose = () => {
    setOverlapDialog({ open: false, slot: null });
  };

  const handleOverlapDialogConfirm = () => {
    if (overlapDialog.slot) {
      addSlotToList(overlapDialog.slot);
    }
    setOverlapDialog({ open: false, slot: null });
  };

  const handleEditTimeSlot = (index) => {
    const slot = maintenanceSlots[index];
    setIsEditing({ isEdit: true, index });
    setNewTimeSlot({
      startTime: slot.startTime,
      endTime: slot.endTime,
    });
  };

  const handleDeleteTimeSlot = (index) => {
    const updatedSlots = maintenanceSlots.filter((_, i) => i !== index);
    setMaintenanceSlots(updatedSlots);
    if (isEditing.isEdit && isEditing.index === index) {
      setIsEditing({ isEdit: false, index: -1 });
      setNewTimeSlot({
        startTime: moment().format('HH:mm'),
        endTime: moment().add(1, 'hour').format('HH:mm'),
      });
    }
  };

  const handleDayChange = (dayName) => {
    const updatedDays = days.map((day) =>
      day.name === dayName ? { ...day, ischecked: !day.ischecked } : day
    );
    setDays(updatedDays);
    
    const selectedDays = updatedDays
      .filter((d) => d.ischecked)
      .map((d) => d.name.toUpperCase());
    
    setMaintenanceFormData({
      recursWeek: { ...recursWeek, value: selectedDays },
    });
  };

  const activityDateRange = getActivityDateRange();
  const rangeStartStr = activityDateRange.start ? activityDateRange.start.format('DD-MM-YYYY') : '';
  const rangeEndStr = activityDateRange.end ? activityDateRange.end.format('DD-MM-YYYY') : '';

  useEffect(() => {
    if (!activityDateRange.start || !activityDateRange.end) return;

    const currentStart = startDate?.value ? moment(startDate.value, 'DD-MM-YYYY') : null;
    const currentEnd = endDate?.value ? moment(endDate.value, 'DD-MM-YYYY') : null;

    const startOutOfRange =
      !currentStart ||
      !currentStart.isValid() ||
      currentStart.isBefore(activityDateRange.start, 'day') ||
      currentStart.isAfter(activityDateRange.end, 'day');
    const newStart = startOutOfRange ? activityDateRange.start.clone() : currentStart;

    const endOutOfRange =
      !currentEnd ||
      !currentEnd.isValid() ||
      currentEnd.isAfter(activityDateRange.end, 'day') ||
      currentEnd.isBefore(newStart, 'day');

    const updates = {};
    if (startOutOfRange) {
      updates.startDate = { ...startDate, value: newStart.format('DD-MM-YYYY'), errors: {} };
    }
    if (endOutOfRange) {
      const nextDay = newStart.clone().add(1, 'day');
      const newEnd = nextDay.isAfter(activityDateRange.end, 'day')
        ? activityDateRange.end.clone()
        : nextDay;
      updates.endDate = { ...endDate, value: newEnd.format('DD-MM-YYYY'), errors: {} };
    }
    if (Object.keys(updates).length) {
      setMaintenanceFormData(updates);
    }
  }, [rangeStartStr, rangeEndStr]);

  useEffect(() => {
    if (recurs?.value === 'WEEK' && startDate?.value && endDate?.value) {
      const availableDays = getDaysOfWeekFromRange(startDate.value, endDate.value);
      const selectedWeek = recursWeek?.value?.map((item) =>
        typeof item === 'string' ? item.toUpperCase() : item?.name?.toUpperCase()
      ) || [];

      const updatedDays = DAYS.map((day) => {
        const isAvailable = availableDays.includes(day.id);
        const wasSelected = selectedWeek.includes(day.name.toUpperCase());
        return {
          ...day,
          isDisable: !isAvailable,
          ischecked: wasSelected && isAvailable,
        };
      });
      setDays(updatedDays);

      const enabledSelectedDays = updatedDays
        .filter((d) => d.ischecked && !d.isDisable)
        .map((d) => d.name.toUpperCase());
      setMaintenanceFormData({
        recursWeek: { ...recursWeek, value: enabledSelectedDays },
      });
    } else {
      setDays(DAYS.map((day) => ({ ...day, ischecked: false, isDisable: false })));
    }
  }, [recurs?.value, startDate?.value, endDate?.value]);

  // Same DD-MM-YYYY handling as the Schedule tab date pickers
  const toStrictMoment = (str) => {
    const m = moment(str, 'DD-MM-YYYY', true);
    return str && m.isValid() ? m : null;
  };

  const handleDateChange = (value, type) => {
    const field = type === 'startDate' ? startDate : endDate;

    if (value === '' || value === null || value === undefined) {
      setMaintenanceFormData({ [type]: { ...field, value: '', errors: {} } });
      return;
    }

    const parsed = toStrictMoment(value);
    if (!parsed || !/^\d{2}-\d{2}-\d{4}$/.test(String(value))) {
      setMaintenanceFormData({
        [type]: { ...field, value, errors: { validDate: 'Invalid date' } },
      });
      return;
    }

    const errors = {};
    const { start, end } = activityDateRange;
    if ((start && parsed.isBefore(start, 'day')) || (end && parsed.isAfter(end, 'day'))) {
      errors.message = 'Date should be within the activity dates';
    } else if (type === 'endDate') {
      const maintStart = toStrictMoment(startDate.value);
      if (maintStart && parsed.isBefore(maintStart, 'day')) {
        errors.message = 'End date cannot be before start date';
      }
    }

    const updates = { [type]: { ...field, value, errors } };

    // keep the end date from falling behind a new start date
    if (type === 'startDate' && !errors.message) {
      const currentEnd = toStrictMoment(endDate.value);
      if (currentEnd && currentEnd.isBefore(parsed, 'day')) {
        updates.endDate = { ...endDate, value, errors: {} };
      }
    }

    setMaintenanceFormData(updates);
  };

  const isValidMonthDates = () => {
    return isValidMonthDatesForValue(recursMonth?.value);
  };

  const isValidMonthDatesForValue = (value) => {
    if (!value || !startDate?.value || !endDate?.value) return false;

    const fromDate = moment(startDate.value, 'DD-MM-YYYY');
    const toDate = moment(endDate.value, 'DD-MM-YYYY');
    const formDataArray = value.split(',').map((d) => d.trim()).filter(Boolean);

    if (!formDataArray.length) return false;

    let isFloat = formDataArray.some((element) => !Number.isInteger(Number(element)));
    if (isFloat) return true;

    const hasInvalidValue = formDataArray.some((d) => {
      const num = parseInt(d, 10);
      return isNaN(num) || num <= 0 || num > 31;
    });
    if (hasInvalidValue) return true;

    const monthData = [];
    const tempFrom = fromDate.clone();
    while (tempFrom <= toDate || tempFrom.format('M') === toDate.format('M')) {
      monthData.push(tempFrom.format('MMMM'));
      tempFrom.add(1, 'month');
      if (monthData.length > 12) break;
    }

    const fromDay = parseInt(fromDate.format('DD'), 10);
    const toDay = parseInt(toDate.format('DD'), 10);

    if (monthData.length === 1) {
      const invalidDate = formDataArray.some((d) => {
        const num = parseInt(d, 10);
        return num < fromDay || num > toDay;
      });
      if (invalidDate) return true;
    } else if (monthData.length === 2) {
      const validDates = [];
      const daysInFirstMonth = moment(startDate.value, 'DD-MM-YYYY').daysInMonth();
      for (let i = fromDay; i <= daysInFirstMonth; i++) validDates.push(i);
      for (let j = 1; j <= toDay; j++) {
        if (!validDates.includes(j)) validDates.push(j);
      }
      const invalidDate = formDataArray.some((d) => !validDates.includes(parseInt(d, 10)));
      if (invalidDate) return true;
    }

    return false;
  };

  const MonthErrorList = () => (
    <div className='input-error'>
      <div className='error-message'>
        <div>Invalid Selected Date</div>
        <div className='error-items'>
          <div>a. Dates cannot contain special character.</div>
          <div>b. Dates cannot contain Decimal, Integer.</div>
          <div>c. Dates cannot contain empty values or '0'.</div>
          <div>d. Dates cannot contain invalid date.</div>
        </div>
      </div>
    </div>
  );

  return (
    <div className='col-md-12 mt-2'>
      <div className='row'>
        <div className='col-md-12'>
          <RadioGroup
            row
            value={maintenanceStatus.value ? 'active' : 'inactive'}
            onChange={(e) => {
              const isActive = e.target.value === 'active';
              setMaintenanceFormData({
                maintenanceStatus: { ...maintenanceStatus, value: isActive },
              });
              if (!isActive) {
                setMaintenanceSlots([]);
              }
            }}
          >
            <FormControlLabel
              value='inactive'
              control={<Radio color='primary' />}
              label='Inactive'
              className='col-md-4'
            />
            <FormControlLabel
              value='active'
              control={<Radio color='primary' />}
              label='Active'
              className='col-md-4'
            />
          </RadioGroup>
        </div>
      </div>

      {maintenanceStatus.value && (
        <>
          <div className='row mt-3'>
            <div className='col-md-12'>
              <RadioGroup
                row
                value={maintenanceType.value}
                onChange={(e) => {
                  setMaintenanceFormData({
                    maintenanceType: { ...maintenanceType, value: e.target.value },
                    recurs: { ...recurs, value: '', errors: {} },
                    recursWeek: { ...recursWeek, value: [], errors: {} },
                    recursMonth: { ...recursMonth, value: '', errors: {} },
                  });
                  setDays(DAYS.map((day) => ({ ...day, ischecked: false, isDisable: false })));
                }}
              >
                <FormControlLabel
                  value='oneTime'
                  control={<Radio color='primary' />}
                  label='One Time'
                  className='col-md-4'
                />
                <FormControlLabel
                  value='recurring'
                  control={<Radio color='primary' />}
                  label='Recurring'
                  className='col-md-4'
                />
              </RadioGroup>
            </div>
          </div>

          <MuiPickersUtilsProvider utils={MomentUtils}>
            <div className='row mt-3'>
              <div className='col-md-6 mb-2'>
                <KeyboardDatePicker
                  variant='inline'
                  margin='normal'
                  id='maintenanceStartDate'
                  label='Start Date'
                  className='col-md-12 mb-2'
                  format='DD-MM-YYYY'
                  required
                  inputValue={startDate.value || ''}
                  onChange={(date, value) => handleDateChange(value, 'startDate')}
                  minDate={activityDateRange.start || undefined}
                  maxDate={activityDateRange.end || undefined}
                  error={!!(startDate.errors?.validDate || startDate.errors?.message)}
                  helperText={startDate.errors?.message || startDate.errors?.validDate}
                />
              </div>
              <div className='col-md-6 mb-2'>
                <KeyboardDatePicker
                  variant='inline'
                  margin='normal'
                  id='maintenanceEndDate'
                  label='End Date'
                  className='col-md-12 mb-2'
                  format='DD-MM-YYYY'
                  required
                  inputValue={endDate.value || ''}
                  onChange={(date, value) => handleDateChange(value, 'endDate')}
                  minDate={toStrictMoment(startDate.value) || activityDateRange.start || undefined}
                  maxDate={activityDateRange.end || undefined}
                  error={!!(endDate.errors?.validDate || endDate.errors?.message)}
                  helperText={endDate.errors?.message || endDate.errors?.validDate}
                />
              </div>
            </div>

            {activityDateRange.start && activityDateRange.end && (
              <div className='row'>
                <div className='col-md-12'>
                  <small className='text-muted'>
                    Schedule maintenance within the activity dates ({activityDateRange.start.format('DD/MM/YYYY')} and {activityDateRange.end.format('DD/MM/YYYY')})
                  </small>
                </div>
              </div>
            )}

            {maintenanceType.value === 'recurring' && (
              <div className='row mt-3'>
                <div className='col-md-6'>
                  <CustomSelect
                    item={{ ...recurs, validations: ['required'] }}
                    handleInputChange={(val) => {
                      setMaintenanceFormData({
                        recurs: { ...recurs, value: val.value, errors: {} },
                        recursWeek: { ...recursWeek, value: [], errors: {} },
                        recursMonth: { ...recursMonth, value: '', errors: {} },
                      });
                      setDays(DAYS.map((day) => ({ ...day, ischecked: false, isDisable: false })));
                    }}
                    labelField='name'
                    valueField='id'
                    options={MAINTENANCE_RECURS_OPTIONS}
                    hideNullOption={true}
                  />
                </div>
              </div>
            )}

            {maintenanceType.value === 'recurring' && recurs.value === 'WEEK' && (
              <div className='row mt-3'>
                <div className='col-md-12 d-flex flex-wrap'>
                  {days.map((day) => (
                    <FormControlLabel
                      key={day.id}
                      control={
                        <Checkbox
                          checked={day.ischecked}
                          onChange={() => handleDayChange(day.name)}
                          color='primary'
                          disabled={day.isDisable}
                        />
                      }
                      label={day.name}
                    />
                  ))}
                </div>
              </div>
            )}

            {maintenanceType.value === 'recurring' && recurs.value === 'MONTH' && (
              <>
                <div className='row mt-3'>
                  <div className='col-md-6'>
                    <CustomTextField
                      item={{ ...recursMonth, validations: ['required'] }}
                      handleInputChange={(val) => {
                        const hasError = isValidMonthDatesForValue(val.value);
                        setMaintenanceFormData({
                          recursMonth: {
                            ...recursMonth,
                            value: val.value,
                            errors: hasError ? { message: 'Invalid Selected Date' } : {},
                          },
                        });
                      }}
                      label='Dates'
                      placeholder='Dates of month in numerical (Separated by commas)'
                    />
                  </div>
                  <div className='col-md-6 d-flex align-items-center'>
                    <small className='text-muted'>(Example: 5, 15, 20)</small>
                  </div>
                </div>
                {(recursMonth.errors?.message || isValidMonthDates()) && <MonthErrorList />}
              </>
            )}

            {maintenanceType.value === 'oneTime' && (
              <div className='row'>
                <div className='col-md-6 mb-2'>
                  <KeyboardTimePicker
                    variant='inline'
                    margin='normal'
                    id='maintenanceStartTime'
                    required
                    label='Start Time'
                    className='col-md-12'
                    ampm={false}
                    format='HH:mm'
                    value={startTime.value ? moment(startTime.value, 'HH:mm') : null}
                    onChange={(time) => {
                      setMaintenanceFormData({
                        startTime: {
                          ...startTime,
                          value: time ? moment(time).format('HH:mm') : '',
                          errors: {},
                        },
                      });
                    }}
                    keyboardIcon={<AccessTimeIcon />}
                  />
                </div>
                <div className='col-md-6 mb-2'>
                  <KeyboardTimePicker
                    variant='inline'
                    margin='normal'
                    id='maintenanceEndTime'
                    required
                    label='End Time'
                    className='col-md-12'
                    ampm={false}
                    format='HH:mm'
                    value={endTime.value ? moment(endTime.value, 'HH:mm') : null}
                    onChange={(time) => {
                      setMaintenanceFormData({
                        endTime: {
                          ...endTime,
                          value: time ? moment(time).format('HH:mm') : '',
                          errors: {},
                        },
                      });
                    }}
                    keyboardIcon={<AccessTimeIcon />}
                  />
                </div>
              </div>
            )}

            {maintenanceType.value === 'recurring' && (
              <>
                <div className='row'>
                  <div className='col-md-6 mb-2'>
                    <KeyboardTimePicker
                      variant='inline'
                      margin='normal'
                      id='maintenanceSlotStartTime'
                      required
                      label='Start Time'
                      className='col-md-12'
                      ampm={false}
                      format='HH:mm'
                      value={newTimeSlot.startTime ? moment(newTimeSlot.startTime, 'HH:mm') : null}
                      onChange={(time) => {
                        setDuplicateError(false);
                        setNewTimeSlot({
                          ...newTimeSlot,
                          startTime: time ? moment(time).format('HH:mm') : '',
                        });
                      }}
                      keyboardIcon={<AccessTimeIcon />}
                    />
                  </div>
                  <div className='col-md-6 mb-2'>
                    <KeyboardTimePicker
                      variant='inline'
                      margin='normal'
                      id='maintenanceSlotEndTime'
                      required
                      label='End Time'
                      className='col-md-12'
                      ampm={false}
                      format='HH:mm'
                      value={newTimeSlot.endTime ? moment(newTimeSlot.endTime, 'HH:mm') : null}
                      onChange={(time) => {
                        setDuplicateError(false);
                        setNewTimeSlot({
                          ...newTimeSlot,
                          endTime: time ? moment(time).format('HH:mm') : '',
                        });
                      }}
                      keyboardIcon={<AccessTimeIcon />}
                    />
                  </div>
                </div>
                <div className='col-md-12 mt-1'>
                  <Button
                    variant='outlined'
                    color='primary'
                    onClick={handleAddTimeSlot}
                    disabled={!newTimeSlot.startTime || !newTimeSlot.endTime}
                    size='large'
                    style={{
                      borderRadius: '2px !important',
                      width: '86px',
                      height: '40px',
                    }}
                  >
                    Add
                  </Button>
                  {duplicateError && (
                    <div className='text-danger mt-2' style={{ fontSize: '14px' }}>
                      Selected dates and times are already in use. Please choose a different time.
                    </div>
                  )}
                </div>

                {maintenanceSlots.length > 0 && (
                  <div className='col-md-12 mt-4'>
                    {maintenanceSlots.map((slot, index) => {
                      const isEditorActive = isEditing.isEdit && isEditing.index === index;
                      return (
                        <div key={index} className='custom-daytime-box py-2 px-4 mb-3'>
                          <div className='d-flex justify-content-between flex-wrap'>
                            <div className='d-flex align-items-center flex-wrap'>
                              <span>{formatSlotDisplay(slot)}</span>
                            </div>
                            <div>
                              {!isEditorActive && (
                                <IconButton
                                  aria-label='edit'
                                  disabled={isEditing.isEdit}
                                  onClick={() => handleEditTimeSlot(index)}
                                >
                                  <EditIcon fontSize='small' />
                                </IconButton>
                              )}
                              <IconButton
                                aria-label='delete'
                                disabled={isEditing.isEdit}
                                onClick={() => handleDeleteTimeSlot(index)}
                              >
                                <RemoveCircle fontSize='small' />
                              </IconButton>
                            </div>
                          </div>
                          {isEditorActive && (
                            <div className='d-flex gap-2 mt-3'>
                              <Button
                                variant='contained'
                                color='primary'
                                size='small'
                                onClick={handleAddTimeSlot}
                              >
                                Save
                              </Button>
                              <Button
                                variant='outlined'
                                color='secondary'
                                size='small'
                                onClick={() => {
                                  setIsEditing({ isEdit: false, index: -1 });
                                  setNewTimeSlot({
                                    startTime: moment().format('HH:mm'),
                                    endTime: moment().add(1, 'hour').format('HH:mm'),
                                  });
                                }}
                              >
                                Cancel
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </MuiPickersUtilsProvider>
        </>
      )}

      {/* Maintenance Overlapping Dialog */}
      <Dialog
        open={overlapDialog.open}
        onClose={handleOverlapDialogClose}
        PaperProps={{
          style: {
            borderRadius: '12px',
            padding: '35px',
            maxWidth: '400px',
          },
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px' }}>
            <WarningAmberRoundedIcon style={{ width: '24px', height: '24px', marginRight: '8px' }} />
            <span style={{ fontSize: '18px', fontWeight: '600' }}>Maintenance Overlapping</span>
          </div>
          <p style={{ color: '#666', margin: '0 0 30px 0', fontSize: '14px', lineHeight: '1.5' }}>
            Selected time slot overlaps with activity time slot, would you like to still proceed?
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
            <Button
              onClick={handleOverlapDialogClose}
              variant='outlined'
              style={{
                borderRadius: '20px',
                minWidth: '100px',
                textTransform: 'none',
                border: '1px solid #ccc',
                color: '#333',
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleOverlapDialogConfirm}
              variant='contained'
              style={{
                borderRadius: '20px',
                minWidth: '100px',
                textTransform: 'none',
                backgroundColor: '#03E67C',
                color: '#333',
              }}
            >
              Save
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default ActivityMaintenance;